import React, { useState } from "react";

interface CustomScrollCalendarProps {
  onDateSelect: (date: Date) => void;
  appliedDates: Date[];
  selectedDates: Date[];
}

const CustomScrollCalendar: React.FC<CustomScrollCalendarProps> = ({
  onDateSelect,
  appliedDates,
  selectedDates,
}) => {
  const today = new Date();
  const [selectedMonth, setSelectedMonth] = useState<number>(today.getMonth());
  const [selectedYear, setSelectedYear] = useState<number>(today.getFullYear());

  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const years = Array.from({ length: 10 }, (_, i) => today.getFullYear() - 5 + i);

  const getDaysInMonth = (year: number, month: number) => {
    const d = new Date(year, month, 1);
    const days: Date[] = [];
    while (d.getMonth() === month) {
      days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  };

  const isApplied = (date: Date) =>
    appliedDates.some(d => d.toDateString() === date.toDateString());

  const isSelected = (date: Date) =>
    selectedDates.some(d => d.toDateString() === date.toDateString());

  const handleDateClick = (date: Date) => {
    onDateSelect(date);
  };

  const cardStyle: React.CSSProperties = {
    maxWidth: 1000,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    padding: 24,
    fontFamily: "Arial, sans-serif",
    marginTop: 10,
  };

  const selectStyle: React.CSSProperties = {
    flex: "1 1 45%",
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 16,
  };

  const gridContainer: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
    gap: 12,
  };

  const days = getDaysInMonth(selectedYear, selectedMonth);

  return (
    <div style={cardStyle}>
      {/* Month & Year pickers */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <select
          value={selectedYear}
          onChange={e => setSelectedYear(+e.target.value)}
          style={selectStyle}
        >
          {years.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>

        <select
          value={selectedMonth}
          onChange={e => setSelectedMonth(+e.target.value)}
          style={selectStyle}
        >
          {months.map((m, idx) => (
            <option key={m} value={idx}>{m}</option>
          ))}
        </select>
      </div>

      {/* Day grid */}
      <div style={gridContainer}>
        {days.map(date => {
          const applied = isApplied(date);
          const selected = isSelected(date);
          const bg = selected ? "#4caf50" : applied ? "white" : "#f9f9f9";
          const color = selected || applied ? "red" : "#333";

          return (
            <div
              key={date.toDateString()}
              onClick={() => handleDateClick(date)}
              style={{
                background: bg,
                color,
                textAlign: "center",
                padding: "14px 6px",
                borderRadius: 10,
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                transition: "transform .2s, background .2s",
              }}
              onMouseEnter={e => {
                if (!selected && !applied) {
                  e.currentTarget.style.background = "#e0f7fa";
                  e.currentTarget.style.transform = "scale(1.1)";
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = "";
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 12 }}>
                {date.toLocaleDateString("en-US", { weekday: "short" })}
              </div>
              <div style={{ fontSize: 20 }}>{date.getDate()}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomScrollCalendar;
